import { Component, Element, Event, EventEmitter, Fragment, h, Host, Prop, State } from '@stencil/core';
import clsx from 'clsx';

import state from '@/store';
import { generateUniqueId } from '@/utils';

interface FdsDropdownSelectedOption {
  value: string;
  index: number;
}

@Component({
  tag: 'fds-dropdown',
  styleUrl: 'styles/dropdown.scss',
  shadow: false,
})
export class Dropdown {
  /**
   * Sets the name attribute on the dropdown
   */
  @Prop() name!: string;

  /**
   * The label for the dropdown
   */
  @Prop() label!: string;

  /**
   * If set, will display a placeholder inside the input field
   */
  @Prop() placeholder!: string;

  /**
   * The id attribute for the dropdown
   */
  @Prop() labelId: string = generateUniqueId('fds-dropdown');

  /**
   * The description of the dropdown. Will be displayed underneath the input field
   */
  @Prop() description?: string;

  /**
   * If true, will render an error icon to the left inside the input field
   */
  @Prop() showIcon = false;

  /**
   * Indicate wether this is a required field in a form or not
   */
  @Prop() required = false;

  /**
   * If true, the dropdow will be disabled and users cannot interact with it
   */
  @Prop() disabled = false;

  @State() selectedValue: string;

  @Element() el: HTMLFdsDropdownElement;

  /**
   * Emitted when the selected value for the dropdown changes
   */
  @Event() fdsDropdownChange: EventEmitter<FdsDropdownSelectedOption>;

  componentDidLoad() {
    const selectedItem = this.getSelectedItem();
    this.selectedValue = selectedItem ? selectedItem.value : this.placeholder;
  }

  private getSelectedItem(): HTMLFdsDropdownItemElement | undefined {
    const itemsArr = [...this.el.querySelectorAll('fds-dropdown-item')] as HTMLFdsDropdownItemElement[];

    return itemsArr.find((item) => item.selected);
  }

  private handleNativeSelectChange(ev: InputEvent): void {
    const selectElement = ev.target as HTMLSelectElement;
    const value = selectElement.value;
    this.selectedValue = value;
    const index = selectElement.selectedIndex;
    this.fdsDropdownChange.emit({ value, index });
  }

  private getOptions(itemsArr: HTMLFdsDropdownItemElement[]): HTMLOptionElement[] {
    return itemsArr.map((item) => {
      const value = item.getAttribute('value') || item.value;
      const isSelected = this.selectedValue === value;
      const text = item.textContent;

      return (
        <option value={value} selected={isSelected} key={value}>
          {text}
        </option>
      );
    });
  }

  private getNotCategorizedItems(): HTMLFdsDropdownItemElement[] {
    return [...this.el.querySelectorAll('fds-dropdown-item:not([category])')] as HTMLFdsDropdownItemElement[];
  }

  render() {
    const {
      el: element,
      name,
      label,
      placeholder,
      labelId,
      description,
      showIcon,
      disabled,
      required,
      selectedValue,
    } = this;

    const containerClasses = clsx({
      'fds-dropdown': true,
      [state.mode]: true,
      'fds-dropdown--disabled': disabled,
    });

    const dropdownClasses = clsx({
      'fds-dropdown__select': true,
      'fds-dropdown__select--withIcon': showIcon,
      'fds-dropdown__select--placeholder': selectedValue === placeholder,
    });

    const dropdownItemsArray = [...element.querySelectorAll('fds-dropdown-item')] as HTMLFdsDropdownItemElement[];

    const options = this.getOptions(dropdownItemsArray);

    const uniqueCategories = [
      ...new Set(dropdownItemsArray.map((item) => item.getAttribute('category')?.toLowerCase() ?? '')),
    ].filter(Boolean);

    const optgroups = uniqueCategories.map((category: string) => {
      const notCategorizedItems = this.getNotCategorizedItems();

      return (
        <Fragment>
          <optgroup label={category} key={category}>
            {dropdownItemsArray
              .filter((item) => item.getAttribute('category') === category)
              .map((item) => {
                const value = item.getAttribute('value');
                const isSelected = selectedValue === value;
                const text = item.textContent;
                return (
                  <option value={value} selected={isSelected} key={value}>
                    {text}
                  </option>
                );
              })}
          </optgroup>
          {notCategorizedItems.length > 0 && this.getOptions(notCategorizedItems)}
        </Fragment>
      );
    });

    return (
      <Host class={containerClasses}>
        <label class="fds-dropdown__label">
          {label && <label htmlFor={labelId}>{label}</label>}
          {required && <span class="fds-dropdown__required">*</span>}
        </label>
        <div class="fds-dropdown__container">
          {showIcon && <fds-icon name="error_filled" class="fds-dropdown__container-error-icon" />}
          <select
            name={name}
            id={labelId}
            class={dropdownClasses}
            onInput={(ev) => this.handleNativeSelectChange(ev)}
            required={required}
          >
            <option value="" hidden>
              {placeholder}
            </option>

            {uniqueCategories.length > 0 ? optgroups : options}
          </select>
          <fds-icon name="expand_more" class="fds-dropdown__container-expand-icon" />
        </div>
        {description && (
          <div class="fds-dropdown__description">
            <fds-icon name="info" size={16} />
            <span>{description}</span>
          </div>
        )}
      </Host>
    );
  }
}
